// This hook gives us a simple way to open Customizr from anywhere in the app.
// You can just call openCustomizr with the customizr config and a callback function 

// e.g.
// const { openCustomizr } = useCustomizr();
// openCustomizr(config, (data) => { console.log("Customizr closed with data:", data); });

// Use React tools we need for this hook.
import { useCallback, useContext } from "react";

// Read the user's auth token from app context.
import { AuthContext } from "global/context/AuthContext";

// Reuse the shared script loader for the Customizr script.
import { useExternalScript } from "./useExternalScript";

// Import the stored app config getter
import { GetAppConfig } from "global/config/AppConfigStore";



// Build the browser details block that the Customizr debugger uses.
function buildCustomizrDebugPayload() {

    // Stop here when we are not running in a browser.
    if (
        typeof window === "undefined" ||
        typeof navigator === "undefined" ||
        typeof document === "undefined"
    ) {
        return {};
    }

    const documentElement = document.documentElement;
    const documentBody = document.body;

    // Read the current browser width.
    const browserWidth =
        window.innerWidth ||
        documentElement?.clientWidth ||
        documentBody?.clientWidth ||
        null;

    // Read the current browser height.
    const browserHeight =
        window.innerHeight ||
        documentElement?.clientHeight ||
        documentBody?.clientHeight ||
        null;

    // Read the screen width if it exists.
    const screenWidth = window.screen?.width || null;

    // Read the screen height if it exists.
    const screenHeight = window.screen?.height || null;

    // Read the browser user agent string.
    const userAgent = navigator.userAgent || "";

    // Read the browser platform value.
    const platform = navigator.platform || "";

    // Read the browser language list, or fall back to one language.
    const languagesArray =
        navigator.languages && navigator.languages.length > 0
            ? navigator.languages
            : [navigator.language || ""];

    // Read the screen direction if the browser gives it to us.
    const orientationType = window.screen?.orientation?.type || null;

    // Guess the screen direction if the browser does not give one.
    const fallbackOrientation = browserWidth >= browserHeight ? "landscape" : "portrait";

    // Return the extra browser details in the shape Customizr expects.
    return {
        browser_width: browserWidth,
        browser_height: browserHeight,
        screen_resolution: screenWidth && screenHeight ? `${screenWidth}x${screenHeight}` : null,
        user_agent: userAgent,
        platform,
        languages: languagesArray.join("-"),
        timezone: Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
        orientation: orientationType || fallbackOrientation,
        device_pixel_ratio: window.devicePixelRatio || 1,
        hardware_concurrency: navigator.hardwareConcurrency || null,
        colorDepth: typeof window.screen?.colorDepth === "number" ? window.screen.colorDepth : null,
    };
}

// Turn caller-friendly settings into the payload Customizr expects.
function normalizeCustomizrConfig(config = {}) {

    // Read the loaded runtime config only when this function is called
    const appConfig = GetAppConfig();

    // Set the logo using the app config customizr logo url
    const DEFAULT_LOGO_URL = appConfig?.customizrLogoUrl || "";

    // Set the safe config
    const safeConfig = config && typeof config === "object" ? config : {};

    // Pull out the known config fields and keep everything else in rest.
    const {
        type = "",
        productCode,
        product_code,
        surfaceCode,
        surface_code,
        printAreaCode,
        print_area_code,
        imageData,
        image_data,
        referrer = typeof window !== "undefined" ? window.location.href : "",
        debug = buildCustomizrDebugPayload(),
        watermark_url = DEFAULT_LOGO_URL,
        customizr_logo_url = DEFAULT_LOGO_URL,
        logo_url = DEFAULT_LOGO_URL,
        site_url = "",
        messages = null,
        ...rest
    } = safeConfig;

    // Return the final config in the names Customizr expects.
    return {
        ...rest,
        type,
        product_code: productCode || product_code || "",
        surface_code: surfaceCode || surface_code || "",
        print_area_code: printAreaCode || print_area_code || "",
        image_data: imageData || image_data || "",
        referrer,
        debug,
        watermark_url,
        customizr_logo_url,
        logo_url,
        site_url,
        messages,
    };

}

// Give the app one simple way to open Customizr.
export function useCustomizr() {

    // Read the loaded runtime config only when this function is called
    const appConfig = GetAppConfig();

    // Get the customizr url from the config
    const customizrUrl = appConfig?.customizrUrl || "";

    // Check whether the Customizr script has finished loading.
    const status = useExternalScript(customizrUrl);
    
    // Use the same token that the API calls already use.
    const { token } = useContext(AuthContext);

    // Open Customizr with the given settings and wait for the result.
    const openCustomizr = useCallback((config, callback) => {

        // Stop here if the script is not ready yet.
        if (status !== "ready") {

            // Tell the caller why Customizr could not open.
            return Promise.reject(new Error("Customizr script not ready"));
 
        }

        // Read Customizr from the window object.
        const strich = window.strich;

        // Stop here if Customizr is missing on the page.
        if (!strich?.customizr?.open) {

            // Tell the caller that Customizr is not available.
            return Promise.reject(new Error("Customizr not available on window"));

        }

        // Add the auth token to the settings we send into Customizr.
        const configWithAuthToken = {
            
            // Normalize the app-friendly config into the payload Customizr expects.
            ...normalizeCustomizrConfig(config),

            // Pass the same auth token we use to make API calls, so Customizr can also call the API using the same credentials.
            authtoken: token || "",

        };

        // Make one final result we can give back after Customizr finishes.
        return new Promise((resolve, reject) => {

            // Remember if we have already finished this call.
            let isSettled = false;
            
            // Remember if Customizr has already sent data back.
            let hasCallbackData = false;
            
            // Keep the data so we can return it at the end.
            let callbackData;
            
            // Decide later if the callback should finish the call straight away.
            let shouldResolveFromCallback = true;

            // Image-return workflows must wait for the callback containing the edited image
            const waitForReturnedImage =
                configWithAuthToken.return_image === true;

            // Finish only once even if Customizr answers more than once.
            const resolveOnce = (data) => {
            
                // Ignore any later result after the first one.
                if (isSettled) {
                    return;
                }

                // Mark this request as finished.
                isSettled = true;

                // Give the result back to the caller.
                resolve(data);

            };

            // Fail only once even if more than one error happens.
            const rejectOnce = (error) => {

                // Ignore any later error after the first one.
                if (isSettled) {
                    return;
                }

                // Mark this request as finished.
                isSettled = true;

                // Give the error back to the caller.
                reject(error);

            };

            // Run the caller's callback and keep the data it gives us.
            const handleResult = async (data, shouldResolveImmediately = true) => {

                // Remember that the callback gave us data.
                hasCallbackData = true;

                // Save the callback data for the final result.
                callbackData = data;

                try {

                    // Run the caller's callback when one was given.
                    if (typeof callback === "function") {
                        await callback(data);
                    }

                    // Finish right away when the callback should end the call.
                    if (shouldResolveImmediately) {
                        resolveOnce(data);
                    }

                } catch (error) {

                    // Fail the whole call if the callback throws.
                    rejectOnce(error);

                }
            };

            try {

                // Run the Customizr callback in the next step.
                const handleCustomizrCallback = (data) => {
                    Promise.resolve().then(() => {

                        // Start with the normal callback resolution behaviour
                        let shouldResolveImmediately =
                            shouldResolveFromCallback;

                        // Image-return workflows must always resolve from this callback
                        if (waitForReturnedImage) {
                            shouldResolveImmediately = true;
                        }

                        // Save the callback result and maybe finish the call
                        void handleResult(
                            data,
                            shouldResolveImmediately
                        );

                    });
                };

                // Open Customizr with the merged settings and callback.
                const openResult = strich.customizr.open(configWithAuthToken, handleCustomizrCallback);

                // If open gave us a close result to wait for, use that to finish the call.
                shouldResolveFromCallback = !(openResult && typeof openResult.then === "function");

                // Wait for Customizr to close when it gives us something to wait for.
                if (openResult && typeof openResult.then === "function") {
                    openResult
                        .then((result) => {

                            // Save the close result if the callback did not already do it.
                            if (!hasCallbackData && result !== undefined) {
                                callbackData = result;
                                hasCallbackData = true;
                            }

                            // An undefined close result is not final when an edited image was requested
                            if (
                                waitForReturnedImage &&
                                !hasCallbackData &&
                                result === undefined
                            ) {
                                return;
                            }

                            // Return callback data when it has already arrived
                            if (hasCallbackData) {
                                resolveOnce(callbackData);
                                return;
                            }

                            // Otherwise return the close result
                            resolveOnce(result);

                        })

                        // Pass close errors back to the caller.
                        .catch(rejectOnce);
                }
            } catch (error) {

                // Pass open errors back to the caller.
                rejectOnce(error);
            }
        });
    }, [status, token]);

    // Expose the open function and simple ready state flags.
    return {

        // Main way to open Customizr.
        openCustomizr,
        
        // True when the script has finished loading.
        isReady: status === "ready",
        
        // True while the script is still loading.
        isLoading: status === "loading",
        
        // True when the script failed to load.
        error: status === "error",
        
        // Expose the raw status as well.
        status,

    };
}
